import { sanitizeCharacterName, isPlaceholderCharacterName } from '../../rankings/components/character-name-utils.js';
import { lastRoundToActiveCharacters, buildListPreviewFromMessages } from '../../rankings/components/state-utils.js';
import { normalizeId } from './id-utils.js';

export const findCharacterNameInCollection = (collection, targetCharacterId) => {
  if (!Array.isArray(collection)) return null;
  const entry = collection.find((item) =>
    String(item?.id ?? item?.characterId ?? item?.character_id ?? '') === String(targetCharacterId)
  );
  return sanitizeCharacterName(entry?.name ?? entry?.characterName ?? entry?.character_name);
};

export const buildConfigCharacterNameMap = (allCharacters) => new Map(
  (Array.isArray(allCharacters) ? allCharacters : [])
    .map((entry) => [
      normalizeId(entry?.id ?? entry?.characterId ?? entry?.character_id),
      sanitizeCharacterName(entry?.name ?? entry?.characterName ?? entry?.character_name)
    ])
    .filter(([id, name]) => id && name)
);

export const resolveBestCharacterName = (targetCharacterId, ...candidateLists) => {
  for (const candidateList of candidateLists) {
    for (const candidate of candidateList) {
      const sanitizedCandidate = sanitizeCharacterName(candidate);
      if (isPlaceholderCharacterName(sanitizedCandidate, targetCharacterId)) continue;
      return sanitizedCandidate;
    }
  }
  return `Character ${targetCharacterId}`;
};

const resolveRemotePrimaryCharacterId = (conv, fallbackCharacterId) => {
  const lastRound = Array.isArray(conv?.lastRound) ? conv.lastRound : [];
  const candidates = [
    conv?.characterId,
    conv?.character_id,
    conv?.meta?.characterId,
    conv?.meta?.character_id,
    lastRound[0]?.characterId,
    lastRound[0]?.character_id,
    fallbackCharacterId
  ];
  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null && String(candidate) !== '') {
      return String(candidate);
    }
  }
  return '';
};

export const conversationIncludesAllCharacters = (metadata, requiredIds) => {
  if (!requiredIds.length) return true;
  const participantIds = new Set();
  const primaryId = normalizeId(metadata?.characterId ?? metadata?.character_id);
  if (primaryId) participantIds.add(primaryId);
  const collections = [
    metadata?.allCharacters,
    metadata?.activeCharacters,
    metadata?.characters
  ];
  for (const collection of collections) {
    if (!Array.isArray(collection)) continue;
    for (const entry of collection) {
      const id = normalizeId(entry?.id ?? entry?.characterId ?? entry?.character_id);
      if (id) participantIds.add(id);
    }
  }
  return requiredIds.every((id) => participantIds.has(String(id)));
};

export const mapRemoteConversations = (conversations, characterId, configCharacterNameMap) =>
  conversations.map((conv) => {
    const meta = conv.meta || {};
    const actualCharacterId = resolveRemotePrimaryCharacterId(conv, characterId);
    const lastRound = Array.isArray(conv.lastRound) ? conv.lastRound : [];
    const allCharacters = lastRoundToActiveCharacters(lastRound).map((entry) => ({
      ...entry,
      name: resolveBestCharacterName(
        entry.id,
        [findCharacterNameInCollection(conv.allCharacters, entry.id), configCharacterNameMap?.get(entry.id)]
      )
    }));
    if (allCharacters.length === 0 && actualCharacterId) {
      allCharacters.push({
        id: actualCharacterId,
        name: resolveBestCharacterName(actualCharacterId, [configCharacterNameMap?.get(actualCharacterId)]),
        outfitId: 0
      });
    }
    const actualCharacterName = resolveBestCharacterName(
      actualCharacterId,
      [
        conv.characterName,
        conv.character_name,
        meta.characterName,
        meta.character_name,
        configCharacterNameMap?.get(actualCharacterId)
      ],
      [
        findCharacterNameInCollection(allCharacters, actualCharacterId),
        findCharacterNameInCollection(conv.allCharacters, actualCharacterId),
        findCharacterNameInCollection(meta.allCharacters, actualCharacterId),
        findCharacterNameInCollection(meta.activeCharacters, actualCharacterId),
        findCharacterNameInCollection(meta.characters, actualCharacterId)
      ]
    );

    return {
      conversationId: conv.conversationId,
      characterId: actualCharacterId,
      characterName: actualCharacterName,
      scenarioId: conv.scenarioId || '',
      greetingVariation: conv.greetingVariation ?? 0,
      greetingVariationCount: conv.greetingVariationCount ?? 1,
      lastLocationId: conv.lastLocationId ?? null,
      lastTime: conv.lastTime ?? null,
      lastRound,
      meta,
      allCharacters,
      isMultiCharacter: allCharacters.length > 1,
      characterCount: allCharacters.length,
      lastMessage: '',
      lastUpdated: meta.lastUpdated || meta.updatedAt || new Date().toISOString(),
      messageCount: meta.messageCount || 0,
      createdAt: meta.createdAt || meta.lastUpdated || new Date().toISOString(),
      source: 'database'
    };
  });

export const mapLocalConversation = ({
  generalMetadata,
  localMessages,
  characterId,
  uniqueRequestedCharacterIds,
  configCharacterNameMap
}) => {
  const localPreview = buildListPreviewFromMessages(localMessages);
  const localCharacters = lastRoundToActiveCharacters(localPreview.lastRound).map((entry) => ({
    ...entry,
    name: resolveBestCharacterName(
      entry.id,
      [
        findCharacterNameInCollection(generalMetadata.allCharacters, entry.id),
        configCharacterNameMap?.get(entry.id)
      ]
    )
  }));
  const requiredIds = Array.isArray(uniqueRequestedCharacterIds) ? uniqueRequestedCharacterIds : [];
  if (requiredIds.length > 0 && !conversationIncludesAllCharacters({
    ...generalMetadata,
    allCharacters: localCharacters.length > 0 ? localCharacters : generalMetadata.allCharacters
  }, requiredIds)) {
    return null;
  }

  const resolvedCharacterId = String(
    characterId
    || generalMetadata.characterId
    || localCharacters[0]?.id
    || ''
  );

  const characterInConversation = (localCharacters.length > 0 ? localCharacters : generalMetadata.allCharacters)?.find((char) =>
    String(char.id) === resolvedCharacterId
  );

  return {
    ...generalMetadata,
    characterId: resolvedCharacterId,
    characterName: resolveBestCharacterName(
      resolvedCharacterId,
      [
        characterInConversation?.name,
        generalMetadata.characterName,
        configCharacterNameMap?.get(resolvedCharacterId)
      ]
    ),
    outfitId: characterInConversation?.outfitId || generalMetadata.outfitId || 0,
    isMultiCharacter: localCharacters.length > 1 || generalMetadata.isMultiCharacter || (generalMetadata.allCharacters?.length > 1),
    characterCount: localCharacters.length || generalMetadata.characterCount || generalMetadata.allCharacters?.length || 1,
    allCharacters: localCharacters.length > 0 ? localCharacters : generalMetadata.allCharacters,
    lastLocationId: localPreview.lastLocationId,
    lastTime: localPreview.lastTime,
    lastRound: localPreview.lastRound,
    isPrimaryCharacter: String(generalMetadata.characterId) === resolvedCharacterId,
    source: 'localStorage'
  };
};

export const enrichLocalConversationMetadata = (conversationId, metadata, messages = []) => {
  let { characterName = 'Unknown Character', lastMessage = '', scenarioName = 'Unknown Scenario' } = metadata;

  if (messages.length > 0) {
    if (!metadata.characterName) {
      const firstAssistantMsg = messages.find((m) => m.role === 'assistant');
      characterName = firstAssistantMsg?.characterName || characterName;
    }
    if (!metadata.lastMessage) {
      lastMessage = messages[messages.length - 1].content || '';
    }
    if (scenarioName === 'Unknown Scenario') {
      const systemMsg = messages.find((m) => m.role === 'system' && m.content?.includes('scenario'));
      const scenarioMatch = systemMsg?.content.match(/scenario[:\s]+([^.!?]+)/i);
      scenarioName = scenarioMatch?.[1]?.trim() || scenarioName;
    }
  }

  return {
    conversationId,
    characterId: metadata.characterId || '',
    characterName,
    scenarioId: metadata.scenarioId || '',
    scenarioName,
    scenarioDescription: metadata.scenarioDescription || metadata.customScenarioDescription || null,
    scenarioType: metadata.scenarioType || (metadata.customScenarioDescription ? 'custom' : 'original'),
    customScenarioDescription: metadata.customScenarioDescription || null,
    backgroundId: metadata.backgroundId || 0,
    backgroundUrl: metadata.backgroundUrl || null,
    locationName: metadata.locationName || '??',
    outfitId: metadata.outfitId || 0,
    locationId: metadata.locationId || 0,
    timeOfDay: metadata.timeOfDay || 'night',
    lastMessage: lastMessage.substring(0, 100),
    lastUpdated: metadata.lastUpdated || new Date().toISOString(),
    messageCount: metadata.messageCount || messages.length,
    createdAt: metadata.lastUpdated || new Date().toISOString(),
    lastSpriteState: metadata.lastSpriteState || metadata.lastEmotion || null
  };
};

export const mergeAndSortConversations = (allConversations) => {
  const seenConversationIds = new Set();
  const uniqueConversations = allConversations.filter((conv) => {
    if (seenConversationIds.has(conv.conversationId)) return false;
    seenConversationIds.add(conv.conversationId);
    return true;
  });
  uniqueConversations.sort((a, b) =>
    new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
  );
  return uniqueConversations;
};
